import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Film } from "@/mocks/films";
import { typeLabels } from "@/utils/catalog";
import { useFavorites } from "@/hooks/useFavorites";

interface HeroCarouselProps {
  slides: Film[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const { isFilmFavorite, toggleFilm } = useFavorites();

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((v) => (v + 1) % slides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const current = slides[index];
  const saved = isFilmFavorite(current.id);

  return (
    <section className="relative overflow-hidden bg-primary-950 text-background-50">
      <div className="absolute inset-0" aria-hidden="true">
        {slides.map((s, i) => (
          <img
            key={s.id}
            src={s.cover}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
              i === index ? "opacity-30" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-900/70" />
      </div>

      <div className="relative max-w-content mx-auto px-4 md:px-6 py-10 md:py-16 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8 lg:gap-12 items-center">
        <div className="mx-auto w-full max-w-[280px] lg:max-w-[320px]">
          <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden border border-primary-700 bg-primary-900">
            <img
              key={current.id}
              src={current.cover}
              alt={`${current.title}海报`}
              title={`${current.title} - 葵司影视资讯`}
              className="w-full h-full object-cover object-top kuisi-fade"
            />
          </div>
        </div>

        <div className="kuisi-fade" key={`info-${current.id}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="px-3 h-7 flex items-center rounded-full bg-accent-500 text-background-950 text-xs font-medium">
              焦点推荐
            </span>
            <span className="px-3 h-7 flex items-center rounded-full bg-primary-800 text-background-200 text-xs">
              {typeLabels[current.type]}
            </span>
          </div>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl leading-tight">
            {current.title}
          </h2>
          <p className="mt-3 text-sm md:text-base text-background-300 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{current.year}</span>
            <span className="text-primary-600">/</span>
            <span>{current.region}</span>
            <span className="text-primary-600">/</span>
            <span>导演 {current.director}</span>
            <span className="text-primary-600">/</span>
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-400" />
              {current.rating.toFixed(1)}
            </span>
          </p>
          <p className="mt-4 max-w-2xl text-sm md:text-base leading-relaxed text-background-200">
            {current.synopsis}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/film/${current.id}`}
              className="px-5 h-11 flex items-center gap-2 rounded-md bg-accent-500 text-background-950 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-accent-400 transition-colors"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <button
              type="button"
              onClick={() => toggleFilm(current.id)}
              className={`px-5 h-11 flex items-center gap-2 rounded-md border text-sm whitespace-nowrap cursor-pointer transition-colors ${
                saved
                  ? "bg-primary-800 border-accent-400 text-accent-300"
                  : "border-primary-600 text-background-100 hover:border-accent-400 hover:text-accent-300"
              }`}
            >
              <i className={saved ? "ri-heart-3-fill" : "ri-heart-3-line"} />
              {saved ? "已收藏" : "加入收藏"}
            </button>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center gap-2">
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  aria-label={`切换到${s.title}`}
                  onClick={() => setIndex(i)}
                  className={`h-2 rounded-full transition-all cursor-pointer ${
                    i === index ? "w-6 bg-accent-500" : "w-2 bg-primary-600"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="上一个"
                onClick={() =>
                  setIndex((v) => (v - 1 + slides.length) % slides.length)
                }
                className="w-8 h-8 flex items-center justify-center rounded-full border border-primary-600 text-background-200 hover:border-accent-400 hover:text-accent-300 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <button
                type="button"
                aria-label="下一个"
                onClick={() => setIndex((v) => (v + 1) % slides.length)}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-primary-600 text-background-200 hover:border-accent-400 hover:text-accent-300 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}